import * as Preact from 'preact';
import * as Hooks from 'preact/hooks';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';

import { useDispatch, useSelector } from './hooks/redux';
import * as Core from '../store/core';
import { AppControlContext } from './context/appControl';
import * as Selectors from '../store/selectors';

export interface Props {
    /** Request that the current layout is exported, with a name provided for the export based on
     * the currently processing Set ID */
    exportGltf: (name: string) => void;
    sceneLoading: boolean;
}

enum State {
    /** No product set IDs to process */
    NONE,
    /**  Loading a Scene */
    LOADING,
    /** Ready to download on command */
    READY,
}

/**
 * Provide a UI to enable looping through a series of product set IDs in turn, triggering the
 * download of a GLB file for each product set
 *
 * With some tweaking, this could be used to trigger a generic action on the complete load of a set.
 *
 * This component currently waits for a user-set amount of time between dora believing the set has
 * finished loading and trying to trigger the download. This is because the load finishes before the
 * placement step is complete, leading to broken GLBs.
 *
 */

export const Component = (p: Props) => {
    const [state, setState] = Hooks.useState(State.NONE);
    const [textValue, setTextValue] = Hooks.useState('');
    const [current, setCurrent] = Hooks.useState(null as null | string);
    const [timeoutMs, setTimeoutMs] = Hooks.useState(2000);
    /**  If true, we download once the timeout has passed - otherwise, wait for a loading button to be pressed */
    const [ignoreLoadButton, setIgnoreLoadButton] = Hooks.useState(true);
    /** If true, we download the GLB - otherwise we don't - turning it off can be useful for
     * checking a bunch of sets without having to download a load of data */
    const [shouldDownloadGlb, setShouldDownloadGlb] = Hooks.useState(true);
    const appControl = Hooks.useContext(AppControlContext);
    const currentUrl = useSelector(Selectors.Core.currentUrl);

    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        const productSetIds = textValue.split('\n');
        if (!p.sceneLoading && productSetIds.length > 0 && state === State.LOADING) {
            // we're loaded
            setState(State.READY);
        } else {
        }
    }, [p.sceneLoading, textValue, state]);

    Hooks.useEffect(() => {
        if (ignoreLoadButton && state === State.READY) {
            const productSetIds = textValue.split('\n');
            if (productSetIds.length >= 1) {
                setTimeout(() => {
                    if (shouldDownloadGlb) {
                        p.exportGltf(current ?? 'export');
                    }
                    if (productSetIds.length > 0) {
                        /** Give the download some time to start */
                        setTimeout(() => {
                            triggerLoad();
                        }, 2000);
                    } else {
                        setState(State.NONE);
                    }
                }, timeoutMs);
            } else {
                setState(State.NONE);
            }
        }
    }, [ignoreLoadButton, state, textValue]);

    const triggerLoad = Hooks.useCallback(() => {
        const productSetIds = textValue.split('\n');
        if (productSetIds.length == 0) {
            return;
        }
        /**
         * WARNING: This overwrites the basic behaviour of the app on close, preventing
         * the iFrame from closing when the 'close' signal is recieved. This should be
         * restored if this is to ever become a proper dev feature and not just a quick
         * hack.
         *
         * The component needs to call closeApp to make sure that the state associated with the
         * current set is cleaned up. How to do this may change or grow more complex over time, so
         * reusing the functionality in closeApp which will be kept up to date seemed wise.
         *
         * However, dealing with opening and closing the iFrame adds further complexity.
         *
         * Updating the 'core_close' part of closing the app avoids this problem, but with the above
         * caveat that it breaks the iFrame closing behaviour of the app after this is called.
         */
        appControl?.enhanceCloseApp('core_close', () => {
            dispatch(Core.setClosed());
        });
        appControl?.closeApp();

        dispatch(Core.setOpen());
        dispatch(
            // TODO: make this work with whatever the current state of productSetIds is.
            Core.startSpaceFromSku({ currentUrl, sku: productSetIds[0] }),
        );
        setCurrent(productSetIds[0]);
        setTextValue(value => value.split('\n').slice(1).join('\n'));
        setState(State.LOADING);
    }, [appControl, currentUrl, textValue]);

    return (
        <Box
            sx={{
                background: 'white',
                border: '1px solid black',
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '500px',
                left: 0,
                overflow: 'scroll',
                p: 2,
                position: 'absolute',
                width: '500px',
            }}
        >
            <Typography>Product Set GLB Downloader</Typography>
            <TextField
                multiline
                value={textValue}
                onChange={e => {
                    /**
                     * We're assuming here that there's a bunch of product set IDs separated only by
                     *  newlines.
                     *
                     * This is the format that you get when copying multiple rows from the same
                     * column in a spreadsheet, which is likely where these IDs are coming from.
                     */
                    setTextValue(e.target.value);
                }}
            />
            <TextField
                value={timeoutMs}
                label="timeout before download (ms)"
                onChange={e => setTimeoutMs(parseInt(e.target.value, 10))}
            />
            <Typography variant="caption">
                Proceeding will break the ability to close the iFrame. Refresh your page once done
                to restore the ability. Be sure not to leave your cursor hovering over the scene, as
                the visible hover state can become baked into the GLBs produced
            </Typography>
            <Button
                disabled={state !== State.NONE}
                onClick={() => {
                    const productSetIds = textValue.split('\n');
                    if (productSetIds.length === 0) {
                        console.log('task already complete');
                        return;
                    }
                    console.log('clearing scene then loading next ID');
                    triggerLoad();
                }}
            >
                Load Sets
            </Button>
            <Button
                disabled={state !== State.READY || ignoreLoadButton}
                onClick={() => {
                    const productSetIds = textValue.split('\n');
                    if (productSetIds.length >= 1) {
                        p.exportGltf(productSetIds[0]);
                        if (productSetIds.length > 1) {
                            triggerLoad();
                        } else {
                            setState(State.NONE);
                        }
                    } else {
                        setState(State.NONE);
                    }
                }}
            >
                Set Loading Complete
            </Button>
            <Typography>Download GLBs?</Typography>
            <Checkbox
                checked={shouldDownloadGlb}
                onChange={e => setShouldDownloadGlb(e.target.checked)}
            />
            <Typography>Run Automatically?:</Typography>
            <Checkbox
                checked={ignoreLoadButton}
                onChange={e => setIgnoreLoadButton(e.target.checked)}
            />
            <Typography>Current Set: {current}</Typography>
        </Box>
    );
};
